import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/reports/export")({
  component: ExportReport,
});

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

const BASE_URL = import.meta.env.VITE_API_URL || "";

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

const CSV_TYPES = [
  { key: "completion-rate", label: "Completion Rate" },
  { key: "review-lag", label: "Review Lag" },
  { key: "unsigned", label: "Unsigned FLHAs" },
  { key: "hazard-frequency", label: "Hazard Frequency" },
  { key: "high-risk", label: "High Risk" },
  { key: "permit-compliance", label: "Permit Compliance" },
  { key: "sub-completion", label: "Subcontractors" },
] as const;

function ExportReport() {
  const params = new URLSearchParams(window.location.search);
  const dateFrom = params.get("dateFrom") || daysAgo(30);
  const dateTo = params.get("dateTo") || today();
  const projectId = params.get("projectId") || undefined;

  const [pdfLoading, setPdfLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState<string | null>(null);
  const [corLoading, setCorLoading] = useState(false);
  const [corError, setCorError] = useState<string | null>(null);

  // COR package options
  const [includeFlhas] = useState(true);
  const [includePdfs, setIncludePdfs] = useState(true);
  const [includeSignatures, setIncludeSignatures] = useState(true);
  const [includePermits, setIncludePermits] = useState(true);

  // Check role
  const { user } = Route.useRouteContext();
  if (user.role !== "admin") {
    return (
      <div className="text-center py-12">
        <p className="text-lg font-medium text-muted-foreground">
          Admin access required
        </p>
        <p className="text-sm text-muted-foreground/70 mt-1">
          Only administrators can access export features.
        </p>
      </div>
    );
  }

  const qs = new URLSearchParams({ dateFrom, dateTo });
  if (projectId) qs.set("projectId", projectId);

  async function handlePdfDownload() {
    setPdfLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/admin/reports/pdf?${qs.toString()}`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to generate PDF");
      const blob = await res.blob();
      triggerDownload(blob, `compliance-report-${dateFrom}-${dateTo}.pdf`);
    } catch (err) {
      console.error(err);
    } finally {
      setPdfLoading(false);
    }
  }

  async function handleCsvDownload(type: string) {
    setCsvLoading(type);
    try {
      const res = await fetch(
        `${BASE_URL}/api/admin/reports/csv/${type}?${qs.toString()}`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to generate CSV");
      const blob = await res.blob();
      triggerDownload(blob, `${type}-${dateFrom}-${dateTo}.csv`);
    } catch (err) {
      console.error(err);
    } finally {
      setCsvLoading(null);
    }
  }

  async function handleCorPackage() {
    setCorLoading(true);
    setCorError(null);
    try {
      const res = await fetch(`${BASE_URL}/api/admin/reports/cor-audit-package`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dateFrom,
          dateTo,
          projectId,
          includeFlhas,
          includePdfs,
          includeSignatures,
          includePermits,
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          (body as { error?: string }).error || `Failed (${res.status})`
        );
      }

      const contentType = res.headers.get("Content-Type") || "";
      if (contentType.includes("application/json")) {
        // Async download case
        const data = (await res.json()) as { downloadUrl: string };
        window.open(data.downloadUrl, "_blank");
      } else {
        const blob = await res.blob();
        triggerDownload(blob, `cor-audit-${dateFrom}-${dateTo}.zip`);
      }
    } catch (err) {
      setCorError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setCorLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Quick Export section */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Quick Export
        </h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={handlePdfDownload}
            disabled={pdfLoading}
            className={cn(
              "px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors",
              pdfLoading
                ? "bg-muted text-muted-foreground cursor-not-allowed"
                : "bg-paris-red text-white hover:bg-paris-red/90"
            )}
          >
            {pdfLoading ? "Generating..." : "Download PDF Report"}
          </button>
        </div>
      </div>

      {/* CSV Downloads */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          CSV Downloads
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {CSV_TYPES.map((csvType) => (
            <button
              key={csvType.key}
              onClick={() => handleCsvDownload(csvType.key)}
              disabled={csvLoading === csvType.key}
              className={cn(
                "px-3 py-2 text-sm font-medium rounded-lg border transition-colors text-left",
                csvLoading === csvType.key
                  ? "bg-muted text-muted-foreground cursor-not-allowed"
                  : "bg-card hover:bg-muted/60"
              )}
            >
              {csvLoading === csvType.key ? "Downloading..." : csvType.label}
            </button>
          ))}
        </div>
      </div>

      {/* COR Audit Package */}
      <div className="border rounded-lg p-4 space-y-4">
        <div>
          <h3 className="text-base font-semibold">COR Audit Package</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Generate a comprehensive audit package containing all FLHAs,
            permits, signatures, and summary reports for COR compliance.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includePdfs}
              onChange={(e) => setIncludePdfs(e.target.checked)}
              className="rounded"
            />
            Include FLHA PDFs
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeSignatures}
              onChange={(e) => setIncludeSignatures(e.target.checked)}
              className="rounded"
            />
            Include Signatures
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includePermits}
              onChange={(e) => setIncludePermits(e.target.checked)}
              className="rounded"
            />
            Include Permits
          </label>
        </div>

        {corError && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {corError}
          </div>
        )}

        <button
          onClick={handleCorPackage}
          disabled={corLoading}
          className={cn(
            "w-full sm:w-auto px-6 py-2.5 text-sm font-medium rounded-lg transition-colors",
            corLoading
              ? "bg-muted text-muted-foreground cursor-not-allowed"
              : "bg-paris-red text-white hover:bg-paris-red/90"
          )}
        >
          {corLoading ? "Generating Package..." : "Generate COR Audit Package"}
        </button>
      </div>
    </div>
  );
}
